import AppContext from 'App/AppContext';
import { IPModal, IPToggle } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ChecklistContainer } from 'components/Checklist/ChecklistContainer';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { globalStackTokensGapSmall } from 'globalStyles';
import Task from 'models/tasks/task';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { canUpdateTaskField, TaskFieldTypes } from '../TaskAuthHelper';
import { getTaskViewModalStyles } from '../View/TaskRenderHelpers';
import { TaskDetailPanel } from './Details/TaskDetailPanel';
import { INormalTaskLayoutProps } from './NormalTaskLayoutProps';
import { TaskNewViewHeader, TaskNewViewLayout, TaskNewViewPanelTabs, useTaskDetailPanelState } from './TaskNewView';
import { getShownTaskPivotKeys } from './taskPivotTabs';

interface INormalTaskDetailLayoutProps extends INormalTaskLayoutProps {
  //remembers the tab the user picked in the side panel
  rememberPivot: (pivot: string) => void;
  //the checklist reports text typed in its add row but not added yet
  onPendingNewItemChange: (hasPendingText: boolean) => void;
}

interface IShowTaskIdToggleProps {
  showTaskId: boolean;
  onChange: (value: boolean) => void;
}

const ShowTaskIdToggle = (props: IShowTaskIdToggleProps) => {
  const { t } = useTranslation(['task']);

  return (
    <IPToggle
      inlineLabel
      label={t('task:Detail.ShowTaskId')}
      checked={props.showTaskId}
      onChange={(_ev, checked) => props.onChange(!!checked)}
      styles={{ root: { marginBottom: 0 } }}
    />
  );
};

// The redesigned task dialog: the checklist (or the form) as the main column and the details, context,
// library and activity in a side panel. Owns the state of that panel.
export const NormalTaskDetailLayout = (props: INormalTaskDetailLayoutProps) => {
  const { t } = useTranslation(['task']);
  const appContext = useContext(AppContext);
  const taskProps = props.taskProps;
  const panelState = useTaskDetailPanelState();
  const [showTaskId, setShowTaskId] = useState<boolean>(
    () => getLocalStorageData(appContext, LocalStorageKeys.TaskShowId) === '1',
  );
  const { panelOpen, fullWindow, onChangePanelOpen } = panelState;
  const canRenameTask = canUpdateTaskField(taskProps.orgTask, taskProps.canUpdate, TaskFieldTypes.Name, appContext);

  const onChangeShowTaskId = (value: boolean) => {
    setShowTaskId(value);
    setLocalStorageData(appContext, LocalStorageKeys.TaskShowId, value ? '1' : '0', false);
  };

  const onSelectPivot = (pivot: string) => {
    taskProps.setSelectedPivot(pivot);
    props.rememberPivot(pivot);
  };

  const onRenameTask = (newName: string) => {
    const copyTask = taskProps.task.clone();
    copyTask.name = newName.replace(/\s*[\r\n]+\s*/g, ' ').trim();
    taskProps.onUpdateTask(copyTask);
  };

  //SingleTask opens a tab of its own accord (the library after an upload, the context for a
  //follow-up): that tab lives in the side panel, so show the panel when it was collapsed
  const prevSelectedPivot = useRef<string | undefined>(taskProps.selectedPivot);
  useEffect(() => {
    const prev = prevSelectedPivot.current;
    prevSelectedPivot.current = taskProps.selectedPivot;
    if (panelOpen || prev === undefined || taskProps.selectedPivot === prev) return;
    if (!taskProps.selectedPivot || taskProps.selectedPivot === 'Checklist' || taskProps.selectedPivot === 'TaskType')
      return;

    onChangePanelOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskProps.selectedPivot]);

  //the Context tab opens the panel and saves the chosen tab
  const showContextTab = () => {
    onChangePanelOpen(true);
    onSelectPivot('Context');
  };

  const getMainColumn = (): JSX.Element => (
    <IPStack verticalFill>
      {props.pivotTabs.hasForm ? (
        props.renderTaskTypeForm(true, false)
      ) : (
        <IPStack.Item grow styles={{ root: { minHeight: 0 } }}>
          <ChecklistContainer
            {...props.checklistProps}
            variant="page"
            onPendingNewItemChange={props.onPendingNewItemChange}
          />
        </IPStack.Item>
      )}
    </IPStack>
  );

  const getPanel = (): JSX.Element => {
    //the checklist and the form are the main column here, so those two pivots show the details
    const mappedPivot =
      !taskProps.selectedPivot || taskProps.selectedPivot === 'Checklist' || taskProps.selectedPivot === 'TaskType'
        ? 'TaskDetails'
        : taskProps.selectedPivot;
    //a tab this task does not show (for example restored from another task) falls back to the
    //details
    const rightPivot = getShownTaskPivotKeys(props.pivotTabs).includes(mappedPivot) ? mappedPivot : 'TaskDetails';

    return (
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <TaskNewViewPanelTabs
          selectedKey={rightPivot}
          onSelect={onSelectPivot}
          onCollapse={() => onChangePanelOpen(false)}
        >
          {props.renderPivotItem('TaskDetails')}
          {props.renderPivotItem('Context')}
          {/* Activity before Library: a narrow pane overflows the last tabs into the menu */}
          {props.renderPivotItem('Activities')}
          {props.renderPivotItem('Resources')}
          {props.renderPivotItem('Instances')}
          {props.renderPivotItem('Audit')}
        </TaskNewViewPanelTabs>
        {rightPivot === 'TaskDetails' && (
          <IPStack.Item grow>
            <TaskDetailPanel
              task={taskProps.task}
              orgTask={taskProps.orgTask}
              onUpdate={(newTask: Task) => {
                taskProps.onUpdateTask(newTask);
              }}
              getErrorMessage={props.validate}
              isActionPending={taskProps.isActionPending}
              loadRescheduleRange={taskProps.loadRescheduleRange}
              tags={taskProps.tags}
              addTagToTaskState={taskProps.addTagToTaskState}
              removeTagFromTaskState={taskProps.removeTagFromTaskState}
              onChangeCompletionDate={taskProps.onChangeCompletionDate}
              canUpdate={taskProps.canUpdate}
            />
          </IPStack.Item>
        )}
        {props.renderPivotPanels(rightPivot)}
      </IPStack>
    );
  };

  return (
    <IPModal
      forceFocusInsideTrap={false}
      //the focusable title is first: opening must not put the focus ring on it
      focusTrapZoneProps={{ disableFirstFocus: true }}
      isOpen={taskProps.isOpen}
      styles={getTaskViewModalStyles(taskProps.windowLevel, fullWindow)}
    >
      {taskProps.isLoading && <OverlayLoader text={t('task:Progress.Save')} />}
      <TaskNewViewLayout
        panelState={panelState}
        header={
          <TaskNewViewHeader
            title={taskProps.task.name}
            taskId={taskProps.task.taskId}
            showTaskId={showTaskId}
            canRename={canRenameTask}
            onRename={onRenameTask}
            panelState={panelState}
            onClose={props.onCancel}
            topBar={props.renderTopBar(false)}
            messages={props.renderMessages(showContextTab)}
          />
        }
        main={getMainColumn()}
        panel={getPanel()}
        footer={props.renderFooter(<ShowTaskIdToggle showTaskId={showTaskId} onChange={onChangeShowTaskId} />)}
        sidePane={taskProps.sidePane}
      />
      {props.children}
    </IPModal>
  );
};
